// src/components/pipelines/nodes/ExitNode.jsx - Exit Point Node for React Flow
import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card.tsx';
import { Badge } from '../../ui/badge.tsx';
import { Input } from '../../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { CheckCircle, XCircle, Flag, LogOut, Edit2 } from 'lucide-react';
import { useReactFlow } from '@xyflow/react';

/**
 * ExitNode - Represents the end point of an automation flow
 *
 * Node data structure:
 * {
 *   id: 'exit-uuid',
 *   type: 'exit',
 *   data: {
 *     label: 'Review Received' | 'No Review' | 'Churned' | etc,
 *     exitType: 'success' | 'failure' | 'neutral',
 *     actions: [{type: 'send_email', ...}],  // Optional exit actions
 *     description: 'Exit point description'
 *   }
 * }
 */
const ExitNode = ({ data, selected, id }) => {
  const {
    label = 'Complete',
    exitType = 'success',
    actions = [],
    description
  } = data;
  const { updateNodeData } = useReactFlow();

  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editedLabel, setEditedLabel] = useState(label);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(description || '');

  // Get icon and color based on exit type
  const getExitConfig = () => {
    switch (exitType) {
      case 'success':
        return {
          icon: CheckCircle,
          color: 'green',
          bgClass: 'from-green-500/10 to-green-600/5',
          borderClass: 'border-green-500/20',
          textClass: 'text-green-100',
          badgeClass: 'border-green-500/50 text-green-300',
          handleClass: '!bg-green-500'
        };
      case 'failure':
        return {
          icon: XCircle,
          color: 'red',
          bgClass: 'from-red-500/10 to-red-600/5',
          borderClass: 'border-red-500/20',
          textClass: 'text-red-100',
          badgeClass: 'border-red-500/50 text-red-300',
          handleClass: '!bg-red-500'
        };
      case 'neutral':
        return {
          icon: Flag,
          color: 'slate',
          bgClass: 'from-slate-500/10 to-slate-600/5',
          borderClass: 'border-slate-500/20',
          textClass: 'text-slate-100',
          badgeClass: 'border-slate-500/50 text-slate-300',
          handleClass: '!bg-slate-500'
        };
      default:
        return {
          icon: LogOut,
          color: 'blue',
          bgClass: 'from-blue-500/10 to-blue-600/5',
          borderClass: 'border-blue-500/20',
          textClass: 'text-blue-100',
          badgeClass: 'border-blue-500/50 text-blue-300',
          handleClass: '!bg-blue-500'
        };
    }
  };

  const config = getExitConfig();
  const Icon = config.icon;

  // Update exit type
  const handleExitTypeChange = (newType) => {
    updateNodeData(id, { ...data, exitType: newType });
  };

  // Save label
  const handleSaveLabel = () => {
    if (editedLabel.trim()) {
      updateNodeData(id, { ...data, label: editedLabel.trim() });
    }
    setIsEditingLabel(false);
  };

  // Save description
  const handleSaveDescription = () => {
    updateNodeData(id, { ...data, description: editedDescription.trim() });
    setIsEditingDescription(false);
  };

  return (
    <div className="exit-node">
      {/* Input handle (left side) - receives from last workflow node */}
      <Handle
        type="target"
        position={Position.Left}
        className={`w-3 h-3 ${config.handleClass}`}
      />

      <Card className={`
        w-56
        ${selected ? `ring-2 ring-${config.color}-500 shadow-lg` : 'shadow-md'}
        transition-all duration-200
        hover:shadow-xl
        bg-gradient-to-br ${config.bgClass}
        ${config.borderClass}
      `}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className={`text-base font-semibold ${config.textClass} flex items-center gap-2`}>
              <Icon className="w-4 h-4" />
              Exit
            </CardTitle>
            <Select value={exitType} onValueChange={handleExitTypeChange}>
              <SelectTrigger className={`w-24 h-6 text-xs ${config.badgeClass} border no-drag`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="success" className="text-slate-200">Success</SelectItem>
                <SelectItem value="failure" className="text-slate-200">Failure</SelectItem>
                <SelectItem value="neutral" className="text-slate-200">Neutral</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent className="pt-0 space-y-2">
          {/* Editable Exit Label */}
          {isEditingLabel ? (
            <Input
              value={editedLabel}
              onChange={(e) => setEditedLabel(e.target.value)}
              onBlur={handleSaveLabel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveLabel();
                if (e.key === 'Escape') setIsEditingLabel(false);
              }}
              autoFocus
              className="text-sm font-medium bg-slate-800 border-slate-700 text-slate-200 no-drag"
            />
          ) : (
            <div
              className="flex items-center gap-2 cursor-pointer no-drag"
              onClick={() => {
                setEditedLabel(label);
                setIsEditingLabel(true);
              }}
            >
              <div className="text-sm text-slate-200 font-medium">{label}</div>
              <Edit2 className="w-3 h-3 text-slate-500 hover:text-slate-300" />
            </div>
          )}

          {/* Exit Actions Count */}
          {actions.length > 0 && (
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <span className="text-slate-400">•</span>
              {actions.length} exit action{actions.length !== 1 ? 's' : ''}
            </div>
          )}

          {/* Editable Description */}
          <div className="border-t border-slate-700/50 pt-2">
            {isEditingDescription ? (
              <Input
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                onBlur={handleSaveDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveDescription();
                  if (e.key === 'Escape') setIsEditingDescription(false);
                }}
                placeholder="Add description..."
                autoFocus
                className="text-xs bg-slate-800 border-slate-700 text-slate-200 no-drag"
              />
            ) : (
              <div
                className="flex items-center gap-2 justify-center cursor-pointer no-drag"
                onClick={() => {
                  setEditedDescription(description || '');
                  setIsEditingDescription(true);
                }}
              >
                {description ? (
                  <div className="text-xs text-slate-400 italic">{description}</div>
                ) : (
                  <div className="text-xs text-slate-500 italic">Add description...</div>
                )}
                <Edit2 className="w-3 h-3 text-slate-500 hover:text-slate-300" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* No output handle - this is the end */}
    </div>
  );
};

export default ExitNode;
